import React, { useEffect } from 'react';
import { type GetServerSideProps } from 'next';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import api from '../../lib/utils/api';
import { mapGiftCardsPageData } from '../../lib/utils/mappers/map-gift-cards-page-data';
import { mapGiftCardData } from '../../lib/utils/mappers/map-gift-card-data';
import {
  type GiftCardsResponse,
  type GiftCardData,
  type GiftCardsPageData,
  type Locale,
  type GiftCardPageResponse,
} from '../../lib/types';
import Featurette from '../../components/Featurette';
import { FEATURETTE_IMAGE_PLACEMENTS } from '../../lib/constants';
import FeaturetteTextContent from '../../components/Featurette/FeaturetteTextContent';
import { GiftCardItem } from '../../components/GiftCards/GiftCardItem';
import cmsEndpoints from '../../lib/utils/cmsEndpoints';
import { PageHead } from '../../components/PageHead';
import { type RootState } from '../../store';
import { convergeTracking } from '../../lib/utils/converge';
import { RichText } from '../../components/RichText';
import { Col, Container, Row } from 'react-bootstrap';

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  );
  const locale = context.locale as Locale;
  const [pageData, giftCardsData] = await Promise.all([
    api.get<GiftCardPageResponse>(cmsEndpoints.giftCardsPage()),
    api.get<GiftCardsResponse>(cmsEndpoints.giftCards()),
  ]);

  if (!pageData || !giftCardsData) {
    return {
      notFound: true,
    };
  }

  const page = mapGiftCardsPageData(pageData?.data?.data?.attributes, locale);
  const giftCards = (giftCardsData?.data?.data ?? []).map((item) =>
    mapGiftCardData(item, locale)
  );

  return {
    props: {
      page,
      giftCards,
    },
  };
};

function GiftCards(props: {
  readonly page: GiftCardsPageData;
  readonly giftCards: GiftCardData[];
}) {
  useEffect(() => {
    const giftCard = props.giftCards[0];
    convergeTracking.trackViewedProductGiftCard(giftCard);
  }, [props.giftCards]);

  const selectCart = (state: RootState) => state.cart;
  const selectCartMemoized = createSelector(selectCart, (cart) => cart);
  const cart = useSelector(selectCartMemoized);

  const emptyPage =
    !props.page.ctaText &&
    !props.page.description &&
    !props.page.image &&
    !props.page.optionsTitle &&
    !props.page.title;

  if (emptyPage) return null;

  return (
    <>
      <PageHead
        title={props.page?.title}
        description={props.page?.description}
        image={props.page?.image}
      />
      <div>
        <Featurette
          mobileFullscreenImage
          image={props.page.image}
          imagePlacement={FEATURETTE_IMAGE_PLACEMENTS.RIGHT}
          backgroundColor="#F8FBFC"
        >
          <div className="h-100 w-100 d-flex align-items-center justify-content-center justify-content-md-start">
            <FeaturetteTextContent
              title={props.page.title}
              body={props.page.description}
            />
          </div>
        </Featurette>

        {props.giftCards.map((giftCard) => (
          <GiftCardItem
            key={giftCard.id}
            giftCard={giftCard}
            disabled={cart.loading || !cart.ready}
          />
        ))}
      </div>

      {props.page.content ? (
        <Container className="mt-4 pb-4">
          <Row>
            <Col>
              <RichText content={props.page.content} />
            </Col>
          </Row>
        </Container>
      ) : null}
    </>
  );
}

export default GiftCards;
